/*
 * @Author: shiguang
 * @Date: 2023-04-25 10:46:45
 * @LastEditors: yusha
 * @LastEditTime: 2024-03-05 15:01:40
 * @Description: App
 */
import React, {
    Suspense,
    useMemo,
    useState,
    useEffect,
    useCallback
} from 'react';
import { ConfigProvider } from 'antd';
import { Locale } from 'antd/lib/locale';
import {
    RouterProvider,
    RouterProviderProps,
    createBrowserRouter
} from 'react-router-dom';
import { useAtom } from 'jotai';
import zh_CN from 'antd/locale/zh_CN';
import ja_JP from 'antd/locale/ja_JP';
import ko_KR from 'antd/locale/ko_KR';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { Local } from '@/model';
import router, { ROUTER_BASENAME, RouteItem } from './config/router';
import '@/styles/theme/b2b-css-variable.scss';
import '@/styles/theme/d2c-css-variable.scss';
import './iconfont/iconfont.css';
import './App.css';
import { getLang, getLocalByLang, LocalContext } from './i18n/i18n';
import 'dayjs/locale/zh';
import 'dayjs/locale/ko';
import 'dayjs/locale/ja';
import { KoWeeks } from './const/config';
const { langType } = Local;

dayjs.extend(updateLocale);

// 更改dayjs的初始化日期。会更改ant-design的日期
dayjs.updateLocale('ko', {
    week: {
        dow: 7
    },
    weekdaysMin: KoWeeks
});

function routerComponentLoading(routeItems?: RouteItem[]) {
    if (!routeItems) {
        return;
    }
    return routeItems.map((item) => {
        const Layout = item.layout;
        if (Layout) {
            return {
                element: (
                    <Layout
                        routes={routeItems}
                        showSlider={item.showSlider}
                        title={item.title}
                    />
                ),
                path: item.path,
                children: routerComponentLoading(item.children),
                errorElement: <div>fref</div>
            };
        }
        const Comp = item.component;
        return {
            element: item.element || (
                <Suspense fallback={<div />}>{Comp && <Comp />}</Suspense>
            ),
            path: item.path,
            children: routerComponentLoading(item.children),
            errorElement: <div>fref</div>
        };
    });
}

const createRouter = (): RouterProviderProps['router'] => {
    return createBrowserRouter(routerComponentLoading(router), {
        basename: ROUTER_BASENAME
    });
};

function App() {
    const [local] = useAtom(langType);
    const [locale, setLocale] = useState<Locale>();

    useEffect(() => {
        const lang = getLang();

        // 设置dayjs为了设置antd里的时间日期语言
        const dayLang = getLocalByLang.get(lang);
        dayjs.locale(dayLang);

        // 设置antd多语言
        const locale = {
            zh_CN,
            ja_JP,
            ko_KR
        }[lang];
        setLocale(locale);
    }, []);

    /** 后续可能根据状态更改 */
    const theme = useMemo(() => {
        return {
            token: {
                colorPrimary: '#21AD8A'
            },
            components: {
                Checkbox: {
                    colorPrimary: '#333333',
                    colorPrimaryHover: '#333'
                },
                Menu: {
                    colorItemTextSelected: '#008060',
                    colorItemText: '#fff',
                    colorBgContainer: '#008060',
                    colorBgBase: '#008060'
                },
                Table: {
                    colorBgContainer: '#fff',
                    colorBorderSecondary: '#fff',
                    controlItemBgHover: 'red'
                },
                Tabs: {
                    colorPrimary: '#008060',
                    colorPrimaryHover: '#008060',
                    lineWidthFocus: 1,
                    fontSize: 18,
                    fontFamily: 'PingFangSC-Semibold, PingFang SC'
                },
                Input: {
                    borderRadius: 2,
                    colorBorder: '#969799',
                    colorPrimaryHover: '#21AD8A',
                    colorText: '#565656'
                },
                InputNumber: {
                    borderRadius: 2,
                    colorBorder: '#969799',
                    colorPrimaryHover: '#21AD8A',
                    colorText: '#565656'
                },
                Select: {
                    borderRadius: 2,
                    colorBorder: '#969799',
                    controlItemBgHover: '#F1FAF7',
                    controlItemBgActive: '#F1FAF7',
                    colorPrimaryHover: '#21AD8A',
                    colorText: '#565656'
                },
                DatePicker: {
                    borderRadius: 2,
                    colorBorder: '#969799',
                    colorPrimaryHover: '#21AD8A',
                    colorText: '#565656'
                },
                Pagination: {}
            }
        };
    }, []);

    const renderEmpty = useCallback(() => {
        return (
            <>
                <div className="nodata-img-cover">
                    <img
                        src="https://static-s.theckb.com/BusinessMarket/smc/nodata.png"
                        alt=""
                        className="nodata-img"
                    />
                    <div>No data</div>
                </div>
            </>
        );
    }, []);

    return (
        <LocalContext.Provider value={local}>
            <ConfigProvider
                locale={locale}
                renderEmpty={renderEmpty}
                theme={theme}
            >
                <div className="App">
                    <RouterProvider
                        router={createRouter()}
                        fallbackElement={<div>fallbackElement</div>}
                    />
                </div>
            </ConfigProvider>
        </LocalContext.Provider>
    );
}

export default App;
